import Carousel from '@brainhubeu/react-carousel';
import { ResizeSensor } from "@blueprintjs/core";
import '@brainhubeu/react-carousel/lib/style.css';
import React from 'react';

import autobind from 'autobind-decorator';
import { IViewProps, View } from 'core/decorators/View';
import ILocalCacheService from 'core/service/cache/ILocalCacheService';
import ServiceRegistry from 'core/util/ServiceRegistry';
import StringUtil from 'core/util/StringUtil';
import styled, { css } from 'styled-components';
import CarouselConfig from './CarouselConfig';
import { NextArrow, PrevArrow, WrapperDiv } from './fragments';

/**
 * @overview container view that wraps each child in a carousel slide
 * @author chills, joe
 */

interface IStyledWrapperProps {
	config: CarouselConfig;
}

const StyledWrapperDiv = styled(WrapperDiv)<IStyledWrapperProps>(({config}) => css`
	& .BrainhubCarousel__arrows {
		background-color: ${config.arrowColor};
		font-size:${config.arrowSize}px;
		opacity:0.5;
	}
	
	& .BrainhubCarousel__arrows:hover {
		background-color: ${config.arrowColor};
  		filter: brightness(85%);
	}
	
	& .BrainhubCarousel__arrows > {
		border-color: ${config.arrowColor};
	}

	.BrainhubCarousel__trackContainer {
		// margin: 0 ${(config.slideSpacing / 2)}px;
	}

	& .slick-dots li.slick-active button:before {
		color:${config.dotsActiveColor};
	}

	& .slick-dots li button:before {
		font-size: ${config.dotsSize}px;
		color: ${config.dotsColor};
		margin: ${config.dotsMargin}
	}
`);

const Slide = styled.div<CarouselConfig>((config) => css`
	margin: 0px ${config.slideSpacing}px;
    width: 100%;
`);

export interface ICarouselViewProps<I extends any | React.ReactNode> extends IViewProps<CarouselConfig> {
	items?: I[]; // uses children if not defined
	getKey?: (item: I) => string;
	skeleton?: boolean; // skeleton enabled. default = true
	renderItem?: (item: I, index?: number, skeleton?: boolean) => React.ReactNode;
	renderItemSkeleton?: (index: number) => React.ReactNode;
}

@View({ id: "carousel-container", configClass: CarouselConfig})
export class CarouselView<I> extends React.PureComponent<ICarouselViewProps<I>, {slides: number}> {
	private container = React.createRef<HTMLDivElement>();

	public constructor(props: ICarouselViewProps<I>) {
		super(props);
		this.state = { slides: this.evaluateSlides() }
	}

	@autobind
	private getStorageKey() {
		const { panelInstance, viewKey } = this.props;
		return StringUtil.format("view:rows:count:%s:%s", panelInstance, viewKey);
	}

	private renderItem = (item: I, index: number, skeleton: boolean) => {
		const { renderItem, renderItemSkeleton, config, getKey } = this.props;
		const key: string = (getKey && getKey(item)) || "slide-" + index;

		return (
			<Slide key={key} {...config}>
				{(skeleton && ((renderItemSkeleton && renderItemSkeleton(index)) || (renderItem && renderItem(null, index, false)))) || (renderItem && renderItem(item)) || item}
			</Slide>
		);
	}

	private onResize = () => {
		this.setState({slides: this.evaluateSlides()})
	}

	private evaluateSlides = () => {
		const { container, props: { config }} = this;

		// default to the config defined value if the ref is not yet established
		if (!config.minSlideWidth || !container.current) {
			return config.slidesToShow;
		}

		// get the size of the slider / container from reference
		const containerWidth = container.current.getBoundingClientRect().width;
		
		// calculate the number of slides to show per page based how many can fit in the container
		const slides = Math.floor(containerWidth / config.minSlideWidth);

		// default to one slide if the container is narrower than the min slide width
		// default to the max slide count even if the container is wide enough to fix more
		return (slides == 0 && 1) || (slides <= config.slidesToShow && slides) || config.slidesToShow;
	}

	public render() {
		const { config, skeleton = true } = this.props;

		const children = React.Children.toArray(this.props.children);
		const items = this.props.items || (children.length > 0 && children) || null;

		let savedRowCount = 0;

		// TODO move this to proper lifecycle functions
		if(skeleton) {
			const storageService = ServiceRegistry.get(ILocalCacheService);
			const key = this.getStorageKey();

			// check if we are reading or saving the row count
			if(Array.isArray(items)) {

				// we have some data, update the row count guess
				storageService.putNumber(key, items.length);

			} else {

				// try to read saved row count
				savedRowCount = storageService.getNumber(key, 0);
			}
		}

		return (
				<StyledWrapperDiv config={config}>
					<ResizeSensor onResize={this.onResize}>
						<div ref={this.container}>
							<Carousel
								slidesPerPage={this.state.slides}
								arrows
								infinite
								animationSpeed={config.autoplay && config.speed ? config.speed : undefined}
      							autoPlay={config.autoplay && config.autoplaySpeed ? config.autoplaySpeed : undefined}
      							stopAutoPlayOnHover
								>
								{Array.isArray(items) ? (
									items.map((v, i) => this.renderItem(v as any, i, false))
								) : (
									Array.from({length: savedRowCount || config.defaultSkeletonCount }, (v, i) => this.renderItem(null, i, true))
								)}
							</Carousel>
						</div>
					</ResizeSensor>
				</StyledWrapperDiv>
		);
	}
}



// WEBPACK FOOTER //
// ./src/common/views/carousel/Carousel.view.tsx